import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import NotificationItem from "./NotificationItem";

const TABS = [
  { id: "all",      label: "Toutes" },
  { id: "unread",   label: "Non lues" },
  { id: "archived", label: "Archivées" },
];

export default function NotificationPanel({ onClose, onNavigate, onUnreadChange }) {
  const [tab, setTab]           = useState("all");
  const [notifs, setNotifs]     = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [unread, setUnread]     = useState(0);

  const load = useCallback(async (t = tab, p = 1) => {
    setLoading(true);
    try {
      const data = await api.notifications.list({ tab: t, page: p });
      if (p === 1) setNotifs(data.notifications || []);
      else setNotifs(prev => [...prev, ...(data.notifications || [])]);
      setTotal(data.total || 0);
      setUnread(data.unread_count || 0);
      onUnreadChange?.(data.unread_count || 0);
    } catch {}
    finally { setLoading(false); }
  }, [tab, onUnreadChange]);

  useEffect(() => { setPage(1); load(tab, 1); }, [tab]);

  const handleRead = async (id) => {
    await api.notifications.markRead(id);
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
    setUnread(u => Math.max(0, u - 1));
    onUnreadChange?.(Math.max(0, unread - 1));
  };

  const handleArchive = async (id) => {
    await api.notifications.archive(id);
    setNotifs(prev => prev.filter(n => n.id !== id));
  };

  const handleReadAll = async () => {
    await api.notifications.markAllRead();
    setNotifs(prev => prev.map(n => ({ ...n, is_read: 1 })));
    setUnread(0);
    onUnreadChange?.(0);
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load(tab, next);
  };

  return (
    <>
      {/* Overlay semi-transparent (clic hors panneau = fermer) */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 299, background: "transparent" }} />

      {/* Panneau slide-in droite */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 300,
        width: 400, maxWidth: "100vw",
        background: "#ffffff", boxShadow: "-4px 0 32px rgba(0,0,0,0.12)",
        display: "flex", flexDirection: "column",
        animation: "slideInRight 0.2s ease-out",
      }}>
        {/* En-tête */}
        <div style={{ padding: "20px 20px 0", borderBottom: "1px solid #f3f4f6" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Notifications</span>
              {unread > 0 && (
                <span style={{ background: "#dc2626", color: "#fff", borderRadius: 12, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {unread > 0 && (
                <button onClick={handleReadAll} style={{ background: "none", border: "1px solid #e5e7eb", color: "#374151", padding: "5px 10px", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>
                  Tout marquer lu
                </button>
              )}
              <button onClick={onClose} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "4px 6px" }}>✕</button>
            </div>
          </div>

          {/* Onglets */}
          <div style={{ display: "flex", gap: 2 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: "7px 14px", border: "none", background: "none",
                fontSize: 13, fontWeight: tab === t.id ? 700 : 400,
                color: tab === t.id ? "#2563eb" : "#6b7280",
                borderBottom: tab === t.id ? "2px solid #2563eb" : "2px solid transparent",
                cursor: "pointer",
              }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Liste */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading && notifs.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9ca3af", fontSize: 13 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
              Chargement…
            </div>
          ) : notifs.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔔</div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, color: "#374151" }}>Aucune notification</div>
              <div style={{ fontSize: 12 }}>Vous êtes à jour !</div>
            </div>
          ) : (
            <>
              {notifs.map(n => (
                <NotificationItem
                  key={n.id}
                  notif={n}
                  onRead={handleRead}
                  onArchive={handleArchive}
                  onNavigate={(page) => { onNavigate?.(page); onClose(); }}
                />
              ))}

              {notifs.length < total && (
                <div style={{ padding: 16, textAlign: "center" }}>
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    style={{ background: "#f9fafb", border: "1px solid #e5e7eb", color: "#374151", padding: "8px 20px", borderRadius: 8, fontSize: 13, cursor: loading ? "not-allowed" : "pointer" }}
                  >
                    {loading ? "Chargement…" : `Charger plus (${total - notifs.length} restantes)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}
